import { useState } from 'react';
import { adoptFieldsFor, type EntityType } from '../../domain/entityTypes.js';
import { isPerEnvironment } from '../../domain/fields.js';
import { AdoptFieldMenu } from './AdoptFieldMenu.js';
import { useConfirmedAdopt } from './AdoptConfirm.js';
import { AdoptToMasterDialog, type AdoptToMasterTarget } from './AdoptToMasterDialog.js';

/**
 * The two per-field adopts, for any host that shows a field diff (the drawer, the push review):
 * **Adopt as override** goes straight to `useConfirmedAdopt`'s confirm; **Adopt to master** opens
 * its dry-run dialog, which the host renders once as `dialog`. `rowActionFor` builds one field's
 * **Adopt ▾** for one record, so the drawer and the push review wire it the same way instead of
 * each hand-building `AdoptFieldMenu`; the host still decides WHICH fields get one (the drawer's
 * currently-differing fields, a push op's patch).
 */
export const useFieldAdoption = () => {
    const adoption = useConfirmedAdopt();
    const [target, setTarget] = useState<AdoptToMasterTarget | null>(null);

    const asOverride = (type: EntityType, key: string, environment: string, fields: string[]) =>
        adoption.adopt(adoptFieldsFor(type, environment, key, fields));

    const rowActionFor =
        ({
            type,
            key,
            environment,
            recordLabel,
        }: {
            type: EntityType;
            key: string;
            environment: string;
            recordLabel: string;
        }) =>
        (group: { field: string; label: string }) => (
            <AdoptFieldMenu
                environment={environment}
                label={group.label}
                onOverride={
                    isPerEnvironment(type, group.field)
                        ? () => asOverride(type, key, environment, [group.field])
                        : undefined
                }
                onMaster={() =>
                    setTarget({
                        type,
                        request: { key, environment, field: group.field },
                        fieldLabel: group.label,
                        recordLabel,
                    })
                }
            />
        );

    return {
        adopt: adoption,
        asOverride,
        rowActionFor,
        dialog: <AdoptToMasterDialog target={target} onClose={() => setTarget(null)} />,
    };
};
